import _fromPairs from "lodash/fromPairs";
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { OutlinedButton } from "common/DS/Buttons";
import { Caption } from "common/DS/Text";
import {
    DEFAULT_MULTI_SELECT_MIN_SELECTIONS,
    DEFAULT_MULTI_SELECT_MAX_SELECTIONS,
    MULTI_SELECT_FOLLOW_UP_DELIMETER,
} from "lib/constants";
import { slugify } from "lib/util/slugify";
import { TranslationFn } from "lib/types";

import AnswerDisplay from "./AnswerDisplay";
import QuestionButtonGroup from "./QuestionButtonGroup";
import { FollowUpProps } from "./types";

type MultiSelectFollowUpProps = FollowUpProps & {
    follow_up_options: NonNullable<FollowUpProps["follow_up_options"]>;
};

const MultiSelectFollowUp = (
    {
        follow_up: followUpText,
        follow_up_options: followUpOptions,
        handleBack,
        handleChange,
        handleSubmit,
        handleSkip,
        optional,
        visible,
        isLastQuestion,
    }: MultiSelectFollowUpProps,
    { t: _ }: { t: TranslationFn },
) => {
    const { options } = followUpOptions;

    /**
     * Because we store the selected options as delimited "comment" text, we need to convert
     * everything back to the source language (english) here when we send it to the server.
     */
    const optionMap = _fromPairs(options.map(({ text, canonical }) => [text, canonical || text]));

    const [selected, setSelected] = useState(_fromPairs(options.map(({ text }) => [text, false])));
    const [isDirty, setIsDirty] = useState(false);

    // Set appropriate defaults for min/max if not specified
    const minSelections = followUpOptions.min_selections ?? DEFAULT_MULTI_SELECT_MIN_SELECTIONS;
    const maxSelections = followUpOptions.max_selections ?? DEFAULT_MULTI_SELECT_MAX_SELECTIONS;

    // Filter to only selected entries and return the value keys delimited by vertical bar
    const renderSelectedValue = () =>
        Object.entries(selected)
            .filter(([, isSelected]) => isSelected)
            .map(([value]) => optionMap[value])
            .join(MULTI_SELECT_FOLLOW_UP_DELIMETER);

    // Toggle the given selection value within the selected state
    const handleSelect = (value: string) => {
        if (!isDirty) setIsDirty(true);
        setSelected((prev) => ({ ...prev, [value]: !prev[value] }));

        if (handleChange) {
            handleChange(renderSelectedValue());
        }
    };

    const createOnSelect = useCallback<
        (value: string) => React.MouseEventHandler<HTMLButtonElement>
    >(
        (value) => (e) => {
            if (e.target instanceof HTMLButtonElement) {
                e.target.blur();
            }
            handleSelect(value);
        },
        [],
    );

    const onSubmit = () => {
        if (handleSubmit) {
            handleSubmit(renderSelectedValue());
        }
    };

    useEffect(() => {
        if (visible && handleChange) {
            handleChange(renderSelectedValue());
        }
    }, [visible]);

    const minMaxText = useMemo(() => {
        if (minSelections >= 2 && maxSelections != null && maxSelections >= 1) {
            return _("Choose between {minSelections} and {maxSelections}", {
                minSelections,
                maxSelections,
            });
        }
        if (minSelections > 1 && !maxSelections) {
            return _("Choose at least {minSelections}", { minSelections });
        }
        if (minSelections <= 1 && maxSelections != null && maxSelections >= 1) {
            return _("Choose up to {maxSelections}", { maxSelections });
        }
        return null;
    }, [minSelections, maxSelections]);

    // Selected and error states
    const totalSelected = Object.values(selected).filter(Boolean).length;
    const minSelected = totalSelected >= minSelections;
    const maxSelected = !!maxSelections && totalSelected >= maxSelections;
    const showError = isDirty && !optional && !minSelected;
    const errorMsg = showError ? _("Choose at least {minSelections}", { minSelections }) : null;

    // Display based state values
    // If <= 5 options then just one column of options, otherwise two columns split evenly
    const midpoint = Math.round(options.length / 2);
    const columns =
        options.length <= 5 ? [options] : [options.slice(0, midpoint), options.slice(midpoint)];

    // If not visible then just hide so we can maintain existing component state
    const style: React.CSSProperties = {};
    if (!visible) {
        style.display = "none";
    }

    return (
        <div
            className="questions__follow-up questions__follow-up-multi-select label-group"
            style={style}
        >
            <label>{followUpText}</label>
            {minMaxText && (
                <Caption className="questions__follow-up-label-caption">{minMaxText}</Caption>
            )}
            <div
                className={classNames("questions__follow-up-multi-select-group", {
                    "questions__follow-up-multi-select-group--single-col": columns.length === 1,
                })}
            >
                {columns.map((colOptions, idx) => (
                    <div
                        className="questions__follow-up-multi-select-group-column"
                        key={`options-col-${idx}`}
                    >
                        {colOptions.map(({ text }) => (
                            <OutlinedButton
                                key={slugify(text)}
                                onClick={createOnSelect(text)}
                                selected={selected[text]}
                                size="large"
                                disabled={!selected[text] && maxSelected}
                            >
                                <AnswerDisplay text={text} />
                            </OutlinedButton>
                        ))}
                    </div>
                ))}
            </div>
            <div className="help-block">{errorMsg}</div>
            <QuestionButtonGroup
                nextDisabled={!minSelected}
                handleSkip={handleSkip}
                handleSubmit={onSubmit}
                handleBack={handleBack}
                isLastQuestion={isLastQuestion}
                forceShowSkip={optional}
                isRequired={!optional}
            />
        </div>
    );
};

MultiSelectFollowUp.contextTypes = {
    t: PropTypes.func,
};

export default MultiSelectFollowUp;
